import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Users2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * Roles por Área — el community map como fuente de verdad de los grupos de
 * Cognito. Muestra el diff (a quién se le agrega/saca qué grupo según su Área)
 * y permite aplicarlo. Corre solo cada día en el cron de las 8am; este panel
 * es para verlo y forzarlo a demanda. `admin` y `clapper` nunca se tocan.
 */
export function AreaRoleSyncPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const { data: plan, isLoading, refetch } = trpc.profile.previewAreaRoleSync.useQuery(undefined, {
    enabled: open,
    staleTime: 30_000,
  });

  const apply = trpc.profile.applyAreaRoleSync.useMutation({
    onSuccess: (r) => {
      toast.success(
        `Roles sincronizados: +${r.added} agregados, −${r.removed} quitados${r.groupsCreated ? `, ${r.groupsCreated} grupos creados` : ''}${r.errors.length ? ` (${r.errors.length} errores)` : ''}`
      );
      utils.profile.previewAreaRoleSync.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const pendingChanges = (plan?.adds.length ?? 0) + (plan?.removes.length ?? 0);

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold flex items-center gap-2">
            <Users2 className="h-4 w-4 text-primary" />
            {t('admin.arearolesyncpanel.roles_por_area_community_map')}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('admin.arearolesyncpanel.la_columna_area_del_community_map')}{' '}
            <code className="text-xs">admin</code> y <code className="text-xs">clapper</code> {t('admin.arearolesyncpanel.nunca_se_tocan')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setOpen(true); refetch(); }}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
          {t('admin.arearolesyncpanel.ver_diff')}
        </Button>
      </div>

      {open && (
        isLoading ? (
          <div className="flex justify-center p-6"><WinclapLoader size={32} className="text-foreground/25" /></div>
        ) : plan ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="secondary">{plan.totalMembers} {t('admin.arearolesyncpanel.activos_en_el_map')}</Badge>
              <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                <CheckCircle2 className="mr-1 h-3 w-3" /> {plan.alreadyInSync} {t('admin.arearolesyncpanel.ya_en_sync')}
              </Badge>
              {plan.groupsToCreate.length > 0 && <Badge>{plan.groupsToCreate.length} {t('admin.arearolesyncpanel.grupos_a_crear')}</Badge>}
              {plan.noCognitoAccount.length > 0 && (
                <Badge variant="outline">{plan.noCognitoAccount.length} {t('admin.arearolesyncpanel.sin_cuenta_en_la_plataforma')}</Badge>
              )}
              {plan.unmappedAreas.length > 0 && (
                <Badge variant="outline">{plan.unmappedAreas.length} {t('admin.arearolesyncpanel.con_area_sin_mapear')}</Badge>
              )}
            </div>

            {pendingChanges === 0 ? (
              <p className="text-sm text-muted-foreground">{t('admin.arearolesyncpanel.todo_en_sync_no_hay_cambios')}</p>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto rounded-lg border divide-y text-sm">
                  {plan.adds.map((c) => (
                    <div key={`a-${c.email}-${c.group}`} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="font-mono text-xs text-green-600 dark:text-green-400 w-5">+</span>
                      <span className="flex-1 truncate">{c.email}</span>
                      <Badge variant="secondary">{c.group}</Badge>
                    </div>
                  ))}
                  {plan.removes.map((c) => (
                    <div key={`r-${c.email}-${c.group}`} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="font-mono text-xs text-red-600 dark:text-red-400 w-5">−</span>
                      <span className="flex-1 truncate">{c.email}</span>
                      <Badge variant="outline">{c.group}</Badge>
                    </div>
                  ))}
                </div>
                <Button
                  disabled={apply.isPending}
                  onClick={() => {
                    if (window.confirm(`¿Aplicar ${pendingChanges} cambio(s) de grupos según el Área del community map?`)) {
                      apply.mutate();
                    }
                  }}
                >
                  {apply.isPending ? <WinclapLoader size={16} className="mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  {t('admin.arearolesyncpanel.sincronizar_ahora')}{pendingChanges})
                </Button>
              </>
            )}

            {plan.unmappedAreas.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('admin.arearolesyncpanel.areas_sin_mapear_quedan_intactos')} {[...new Set(plan.unmappedAreas.map((u) => u.area))].join(', ')}
              </p>
            )}
          </div>
        ) : null
      )}
    </div>
  );
}
